import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import {
  BreadcrumbItem,
  BreadcrumbsComponent,
} from '../../../../shared/components/breadcrumbs/breadcrumbs.component';
import { UserRole } from '../../../../core/auth/models/auth.models';
import { UserApiModel } from '../../models/user.models';
import { UsersService } from '../../services/users.service';

type UserRoleFilter = UserRole | 'all';
type UserStatusFilter = 'all' | 'enabled' | 'disabled';

@Component({
  selector: 'app-users',
  imports: [CommonModule, FormsModule, RouterLink, BreadcrumbsComponent],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersComponent {
  private readonly usersService = inject(UsersService);

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly users = signal<UserApiModel[]>([]);

  readonly roleFilter = signal<UserRoleFilter>('all');
  readonly statusFilter = signal<UserStatusFilter>('all');
  readonly search = signal('');

  readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', route: '/home' },
    { label: 'Users' },
  ];

  readonly totalUsers = computed(() => this.users().length);

  readonly filteredUsers = computed(() => {
    const roleFilter = this.roleFilter();
    const statusFilter = this.statusFilter();
    const search = this.search().trim().toLowerCase();

    return this.users().filter((user) => {
      const roleMatches = roleFilter === 'all' || user.role === roleFilter;

      const statusMatches =
        statusFilter === 'all'
          ? true
          : statusFilter === 'enabled'
            ? user.isActive
            : !user.isActive;

      const searchMatches =
        !search ||
        user.fullName.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search);

      return roleMatches && statusMatches && searchMatches;
    });
  });

  readonly roleCounts = computed(() => {
    const users = this.users();
    return {
      all: users.length,
      admin: users.filter((user) => user.role === 'admin').length,
      dekalb: users.filter((user) => user.role === 'dekalb').length,
      partner: users.filter((user) => user.role === 'partner').length,
    };
  });

  readonly statusCounts = computed(() => {
    const users = this.users();
    return {
      all: users.length,
      enabled: users.filter((user) => user.isActive).length,
      disabled: users.filter((user) => !user.isActive).length,
    };
  });

  constructor() {
    this.loadUsers();
  }

  setRoleFilter(value: UserRoleFilter): void {
    this.roleFilter.set(value);
  }

  setStatusFilter(value: UserStatusFilter): void {
    this.statusFilter.set(value);
  }

  updateSearch(value: string): void {
    this.search.set(value);
  }

  private loadUsers(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.usersService
      .listUsers()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ data }) => {
          this.users.set(data);
        },
        error: (error) => {
          this.errorMessage.set(error?.error?.message ?? 'Unable to load users right now.');
        },
      });
  }
}